import { mstCommon } from '@libs/domain';
import type { LedgerType } from '@libs/enums';
import { customFieldAttribute } from '@libs/enums';
import { mstRegistrationFieldConfig } from '@libs/templates/mst';
import { Characters, Item, ItemName, Locale, WithExclusion } from '@ps-smart-cp/ppfw-client';
import type { CustomFieldSetting } from '../hooks';
import { CustomFieldBasicSettingProvider } from '../models';
import type { CustomFieldLedgerSetting, SingleCustomField, SingleCustomMstRange } from '../models';

function getMetaCodeMst(customFieldKey: number, basicSettings: CustomFieldBasicSettingProvider[]) {
  return basicSettings.find(({ key }) => key === customFieldKey)!;
}

function createCustomMstItemName(
  customFieldKey: number,
  basicSettings: CustomFieldBasicSettingProvider[],
  prefix = '',
) {
  return ItemName.of([Locale.ja, getMetaCodeMst(customFieldKey, basicSettings).displayName + prefix]);
}

function createCustomMstItemAttribute(metaCodeMst?: CustomFieldBasicSettingProvider, required = false) {
  return Characters.preset.halfWidth(
    metaCodeMst ? { minLen: metaCodeMst.length, maxLen: metaCodeMst.length, required } : {},
  );
}

function createCustomMstItem(customFieldKey: number, basicSettings: CustomFieldBasicSettingProvider[]) {
  return {
    code: Item.of(
      mstCommon.names.code,
      createCustomMstItemAttribute(getMetaCodeMst(customFieldKey, basicSettings), true),
    ),
  };
}

function createCustomMstFreeFormatItemAttribute(required = false) {
  return Characters.preset.freeFormat({ maxLen: 25, required: required });
}

function createCustomFieldFreeFormatItem(
  customFieldKey: number,
  basicSettings: CustomFieldBasicSettingProvider[],
  required = false,
) {
  return {
    customFieldValue: Item.of(
      createCustomMstItemName(customFieldKey, basicSettings),
      createCustomMstFreeFormatItemAttribute(required),
    ),
  };
}

function createCustomFieldFreeFormatValidateItem(
  customFieldKey: number,
  basicSettings: CustomFieldBasicSettingProvider[],
  required = false,
) {
  return {
    [`customFieldValue${customFieldKey > 9 ? customFieldKey : `0${customFieldKey}`}`]: Item.of(
      createCustomMstItemName(customFieldKey, basicSettings),
      createCustomMstFreeFormatItemAttribute(required),
    ),
  };
}

function createCustomFieldItem(
  customFieldKey: number,
  basicSettings: CustomFieldBasicSettingProvider[],
  required = false,
) {
  return {
    customFieldValue: Item.of(
      createCustomMstItemName(customFieldKey, basicSettings),
      createCustomMstItemAttribute(getMetaCodeMst(customFieldKey, basicSettings), required),
    ),
  };
}

function createCustomMstCodeValidateItem(
  customFieldKey: number,
  basicSettings: CustomFieldBasicSettingProvider[],
  required = false,
) {
  return {
    [`customMstCode${customFieldKey > 9 ? customFieldKey : `0${customFieldKey}`}`]: Item.of(
      createCustomMstItemName(customFieldKey, basicSettings),
      createCustomMstItemAttribute(getMetaCodeMst(customFieldKey, basicSettings), required),
    ),
  };
}

function createCustomValidateItem(setting: CustomFieldSetting) {
  const customFieldBasicSettingProvider = CustomFieldBasicSettingProvider.fromSetting(setting.basicSetting);
  if (setting.basicSetting.attribute === customFieldAttribute.enum.mstCode) {
    return {
      customFieldValue: Item.of(
        createCustomMstItemName(setting.customFieldKey, [customFieldBasicSettingProvider]),
        createCustomMstItemAttribute(
          getMetaCodeMst(setting.customFieldKey, [customFieldBasicSettingProvider]),
          setting.ledgerSettingDetail.isRequired,
        ),
      ),
    };
  } else {
    return {
      customFieldValue: Item.of(
        createCustomMstItemName(setting.customFieldKey, [customFieldBasicSettingProvider]),
        createCustomMstFreeFormatItemAttribute(setting.ledgerSettingDetail.isRequired),
      ),
    };
  }
}

function createCustomMstCodeRangeItem(
  customFieldKey: number,
  basicSettings: CustomFieldBasicSettingProvider[],
  suffix: string,
) {
  return {
    customMstCodeRange: Item.of(
      createCustomMstItemName(customFieldKey, basicSettings, suffix),
      WithExclusion.of(Characters.preset.alphanumericalsRange({ maxLen: 250 })),
    ),
  };
}

function createCustomMstRegistrationFieldConfig(
  customFieldKey: number,
  basicSettings: CustomFieldBasicSettingProvider[],
) {
  return {
    ...mstRegistrationFieldConfig,
    ...createCustomMstItem(customFieldKey, basicSettings),
  };
}

function createCustomMstCodeRangeBodies(customCodeRanges: SingleCustomMstRange[]) {
  return Object.fromEntries(
    customCodeRanges.map((curr) => [curr.customFieldKey.toString(), curr.customMstCodeRange.body]),
  );
}

function createCustomMstCodeRangeExclusions(customCodeRanges: SingleCustomMstRange[]) {
  return Object.fromEntries(
    customCodeRanges.map((curr) => [curr.customFieldKey.toString(), curr.customMstCodeRange.isExcluded]),
  );
}

function createCustomMstFromEntries(customMsts: SingleCustomField[]) {
  return Object.fromEntries(
    customMsts.map((curr) => [curr.customFieldKey.toString(), curr.customFieldValue]),
  );
}

function createSingleCustomMstFromEntries(customMsts: Record<string, string>) {
  return Object.entries(customMsts).map((curr) => {
    return { customFieldKey: Number(curr[0]), customFieldValue: curr[1] };
  });
}

const isCustomFieldRequired = (
  customFieldKey: number,
  ledgerSetting: CustomFieldLedgerSetting[],
  ledgerType: LedgerType,
) => {
  return (
    ledgerSetting
      .find((l) => l.ledger === ledgerType)
      ?.detail.find((d) => Number(d.customField) === customFieldKey)?.isRequired ?? false
  );
};

export {
  createCustomFieldItem,
  createCustomMstCodeRangeBodies,
  createCustomMstCodeRangeExclusions,
  createCustomMstCodeRangeItem,
  createCustomMstFromEntries,
  createCustomMstItem,
  createCustomMstItemAttribute,
  createCustomMstItemName,
  createCustomMstRegistrationFieldConfig,
  createSingleCustomMstFromEntries,
  getMetaCodeMst,
  createCustomFieldFreeFormatItem,
  createCustomMstFreeFormatItemAttribute,
  createCustomMstCodeValidateItem,
  createCustomFieldFreeFormatValidateItem,
  isCustomFieldRequired,
  createCustomValidateItem,
};
